import { type AsyncResult, fail, ok } from "operation-result.js";
import { AppError } from "trunk.js";
import type { MerchantSession } from "../auth/merchant-session";
import { thrownText } from "../primitives/errors";
import type { ProfileRepository } from "../repositories/profile-repository";
import type { BackendApi } from "./api";

/** One sentence per sign-in path; the Firebase error is kept as `cause` for the console. */
export class SignInFailed extends AppError {}

/** The merchant closed the provider's popup: nothing to report, but the caller may want to know. */
export class SignInCancelled extends AppError {
  constructor(cause?: unknown) {
    super("Sign-in was cancelled.", cause);
  }
}

function firebaseCode(error: unknown): string {
  return typeof error === "object" && error !== null && "code" in error ? String(error.code) : "";
}

/**
 * Loads the merchant's profile for the signed-in Firebase user. The backend
 * creates the row from the ID token on first sight, so a failure here means the
 * request itself failed (backend down, 401, 5xx), never "no account yet". A
 * refused Result and a thrown transport error are the same outcome to the
 * screen, so both land on `failed`. Single flight.
 */
export async function loadProfile(o: {
  client: BackendApi;
  session: MerchantSession;
  profileRepository: ProfileRepository;
  fresh?: boolean;
}): Promise<void> {
  if (o.profileRepository.status.kind === "loading") return;
  o.profileRepository.set({ kind: "loading" });
  try {
    const r = await o.client.user.me(await o.session.authorizeCall({ fresh: o.fresh }));
    if (r.ok) o.profileRepository.set({ kind: "loaded", profile: r.value });
    else o.profileRepository.set({ kind: "failed", message: r.errors[0].message });
  } catch (e) {
    o.profileRepository.set({ kind: "failed", message: thrownText(e, "Could not load your account.") });
  }
}

async function attempt(fn: () => Promise<void>, message: string): AsyncResult<void, SignInFailed | SignInCancelled> {
  try {
    await fn();
    return ok();
  } catch (e) {
    const code = firebaseCode(e);
    if (code === "auth/popup-closed-by-user" || code === "auth/cancelled-popup-request") {
      return fail(new SignInCancelled(e));
    }
    return fail(new SignInFailed(message, e));
  }
}

export function signInWithEmail(o: {
  session: MerchantSession;
  email: string;
  password: string;
}): AsyncResult<void, SignInFailed | SignInCancelled> {
  return attempt(() => o.session.signInWithEmail(o.email, o.password), "Invalid email or password.");
}

/**
 * Creates the account, then re-syncs the profile: the auth listener loads it
 * the moment Firebase signs the new user in, before the display name is set,
 * and this corrects the row it created.
 */
export async function signUpWithEmail(o: {
  client: BackendApi;
  session: MerchantSession;
  profileRepository: ProfileRepository;
  name: string;
  email: string;
  password: string;
}): AsyncResult<void, SignInFailed | SignInCancelled> {
  const r = await attempt(
    () => o.session.signUpWithEmail(o.name, o.email, o.password),
    "Registration failed. The email may already be in use.",
  );
  if (!r.ok) return r;
  await loadProfile({ ...o, fresh: true });
  return ok();
}

export function signInWithGoogle(o: { session: MerchantSession }): AsyncResult<void, SignInFailed | SignInCancelled> {
  return attempt(() => o.session.signInWithGoogle(), "Google sign-in failed.");
}

export function signInWithApple(o: { session: MerchantSession }): AsyncResult<void, SignInFailed | SignInCancelled> {
  return attempt(() => o.session.signInWithApple(), "Apple sign-in failed.");
}

export function sendMagicLink(o: {
  session: MerchantSession;
  email: string;
}): AsyncResult<void, SignInFailed | SignInCancelled> {
  return attempt(() => o.session.sendMagicLink(o.email), "Could not send the magic link.");
}

export async function signOut(o: { session: MerchantSession }): Promise<void> {
  await o.session.signOut();
}
